"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { NotFoundPath, safeDecode } from "@/lib/not-found/not-found-path";

export type NotFoundCandidate = {
  kind: "FAQ" | "Blog" | "Game";
  href: string;
  // The slug the candidate lives at (FAQ/blog id, game id) and its human title.
  id: string;
  title: string;
};

const SECTION_KINDS: Record<string, NotFoundCandidate["kind"]> = {
  faq: "FAQ",
  blog: "Blog",
  apps: "Game",
};

function words(text: string) {
  return text
    .toLowerCase()
    .replace(/['’]/g, "")
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length >= 3);
}

// Levenshtein, capped: only near-misses of the slug (a typo, a renamed suffix) matter here.
function editDistance(a: string, b: string) {
  if (Math.abs(a.length - b.length) > 6) return 99;
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return previous[b.length];
}

function score(
  slug: string,
  slugWords: string[],
  candidate: NotFoundCandidate,
) {
  const id = candidate.id.toLowerCase();
  if (id === slug) return 100;
  let points = 0;
  if (id.startsWith(slug) || slug.startsWith(id)) points += 6;
  const distance = editDistance(slug, id);
  if (distance <= 3) points += 8 - distance * 2;
  const haystack = new Set([...words(candidate.id), ...words(candidate.title)]);
  for (const word of slugWords) {
    if (haystack.has(word)) {
      points += 3;
    } else if ([...haystack].some((other) => other.startsWith(word))) {
      points += 1;
    }
  }
  return points;
}

// Reads the missing URL in the browser (not-found pages get no params) and lists the closest
// existing pages: a mistyped FAQ id finds its FAQ entry, an old blog slug its post.
export function NotFoundSuggestions({
  candidates,
}: {
  candidates: NotFoundCandidate[];
}) {
  const pathname = usePathname() ?? "/";
  const segments = pathname.split("/").filter(Boolean);
  const sectionKind = SECTION_KINDS[segments[0] ?? ""];
  const slug = safeDecode(segments.at(-1) ?? "").toLowerCase();
  const slugWords = words(slug);

  const suggestions =
    slug.length < 3
      ? []
      : candidates
          .filter((candidate) => !sectionKind || candidate.kind === sectionKind)
          .map((candidate) => ({
            candidate,
            points: score(slug, slugWords, candidate),
          }))
          .filter(({ points }) => points >= 3)
          .sort((a, b) => b.points - a.points)
          .slice(0, 4)
          .map(({ candidate }) => candidate);

  return (
    <>
      <NotFoundPath />
      {suggestions.length > 0 && (
        <div className="w-full max-w-xl text-left">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Did you mean
          </h2>
          <ul className="divide-y divide-neutral-800 overflow-hidden rounded-lg border border-neutral-800 bg-zinc-900/60">
            {suggestions.map((suggestion) => (
              <li key={suggestion.href}>
                <Link
                  href={suggestion.href}
                  className="group flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/60 transition-colors"
                >
                  <span className="w-12 shrink-0 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {suggestion.kind}
                  </span>
                  <span className="grow">{suggestion.title}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
